import assert from 'assert';
import React, {useCallback, useLayoutEffect, useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {shallowEqual} from 'react-redux';

import {makeReactContext} from 'lib/makeReactContext';
import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {useTableSpreadsheetStore} from 'notebook/tables/store/hooks/useTableSpreadsheetStore';
import {createTableModelSelector, TableModel} from 'notebook/tables/store/models/TableModel';
import {
  createTableEditableStore,
  DEFAULT_TABLE_EDITABLE_REDUX_STATE,
  TableEditableReduxState,
  TableEditableStore,
} from 'notebook/tables/store/TableStore';

interface TableContextInterface {
  useTableEditableStore: ReturnType<typeof createTableEditableStore>;
  tableModelSelector: (state: TableEditableStore) => TableModel;
  setState: (state: Partial<TableEditableState>) => void;
}

export const TableModelContext = makeReactContext<TableContextInterface>(() => {
  assert(false, 'TableContext must be used within a TableStateWrapper');
}, 'StructuredTableContext');

export const TableModelProvider = function ({
  children,
  tableProps,
  reduxState = DEFAULT_TABLE_EDITABLE_REDUX_STATE,
}: {
  children: React.ReactNode;
  tableProps: TableEditableProps;
  /**
   * Redux-derived data populated into the store before the model is generated. Passed in as an
   * opaque object (rather than as TableEditable props) so that it stays internal store state and
   * does not pollute the component's internal or external props. Callers are responsible for
   * creating and memoizing it. Omitted in contexts (e.g. the cross-step formula box) where no
   * redux-derived data is applicable.
   */
  reduxState?: TableEditableReduxState;
}): JSX.Element {
  const {
    table,
    staleRows,
    errorRows,
    warningRows,
    computedTable,
    showExcessLabels: overrideShowExcessLabels,
    ...restStableProps
  } = tableProps;

  // A table missing from the computed-table map arrives here as `undefined`. Default it to the
  // placeholder `[[]]` at the store boundary so both the store selectors and TableEditable read a
  // defined table, while callers upstream of the store keep the real `undefined` (letting their
  // `!= null` checks distinguish "not evaluated" from "empty").
  const definedComputedTable = useMemo(() => computedTable ?? [[]], [computedTable]);

  // `documentName` is only reliably available from the DocumentEditorContext (it is not threaded
  // through as a component prop), so source it from context here to keep the store consistent with
  // what the component reads. See TableEditableComponent's pass-through of `documentName`.
  const {documentName} = DocumentEditorContext.useContext();
  const stableProps = useMemo(() => ({...restStableProps, documentName}), [restStableProps, documentName]);
  const volatileProps = useMemo(
    () => ({table, staleRows, errorRows, warningRows, computedTable: definedComputedTable}),
    [table, staleRows, errorRows, warningRows, definedComputedTable]
  );
  const {tableNamespace, disableFormulas} = restStableProps;

  // Spreadsheet-store-derived state (e.g. `showExcessLabels`) is subscribed to in this hook and
  // pushed into the store's `spreadsheetState` so that only the axis-cell models re-render on a
  // formula selection.
  const spreadsheetState = useTableSpreadsheetStore(tableNamespace, disableFormulas ?? false, {
    overrideShowExcessLabels,
  });

  // Extract editor properties from context to store separately.
  const editorContext = EditorPropertiesContext.useContext();

  // This should explicitly only be created once, and use the initial prop
  const useTableEditableStore = useMemo(
    () => createTableEditableStore({stableProps, volatileProps, editorContext, reduxState, spreadsheetState}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {setTableState, setTableProps, setEditorContext, setReduxState, setSpreadsheetState} =
    useTableEditableStore((store) => ({
      setTableState: store.setState,
      setTableProps: store.setProps,
      setEditorContext: store.setEditorContext,
      setReduxState: store.setReduxState,
      setSpreadsheetState: store.setSpreadsheetState,
    }));

  const tableModelSelector = useMemo(() => createTableModelSelector(), []);

  const setState = useCallback(
    (state: Partial<TableEditableState>) => {
      const previousState = useTableEditableStore.getState().state;
      const newState = {
        ...previousState,
        ...state,
      };
      if (!isEqual(newState, previousState)) {
        setTableState(newState);
      }
    },
    [setTableState, useTableEditableStore]
  );
  const contextValue: TableContextInterface = useMemo(
    () => ({tableModelSelector, setState, useTableEditableStore}),
    [tableModelSelector, setState, useTableEditableStore]
  );

  // These sync the provider's inputs (props, editor context, redux- and spreadsheet-derived state)
  // into the store, which is the ONLY path by which the memoized `TableEditableComponent` and the
  // rows below it learn about a change: they read from the store, not from these props, and the memo
  // keeps them from re-rendering when this provider re-renders. They must be `useLayoutEffect`, not
  // `useEffect`: a store write here notifies subscribers and schedules their re-render, and only
  // layout effects flush that re-render synchronously — within the same commit that handled the
  // triggering interaction. With a passive `useEffect` the store write (and the subscribers' render)
  // is deferred to a later task, so code that reads the DOM/model right after the interaction (e.g. a
  // paste followed by an assertion) observes the pre-update state. See the double-commit that
  // `React.memo(TableEditableComponent)` otherwise removes.
  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (storeState.volatileProps !== volatileProps || !shallowEqual(storeState.stableProps, stableProps)) {
      setTableProps(stableProps, volatileProps);
    }
  }, [useTableEditableStore, setTableProps, volatileProps, stableProps]);

  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (!isEqual(storeState.editorContext, editorContext)) {
      setEditorContext(editorContext);
    }
  }, [useTableEditableStore, setEditorContext, editorContext]);

  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (!isEqual(storeState.reduxState, reduxState)) {
      setReduxState(reduxState);
    }
  }, [useTableEditableStore, setReduxState, reduxState]);

  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (!isEqual(storeState.spreadsheetState, spreadsheetState)) {
      setSpreadsheetState(spreadsheetState);
    }
  }, [useTableEditableStore, setSpreadsheetState, spreadsheetState]);

  return <TableModelContext.Provider value={contextValue}>{children}</TableModelContext.Provider>;
};
